import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import {
  isPastAcademicYear,
  missingAcademicYearMessage,
  useCurrentAcademicYear,
} from '@/hooks/useCurrentAcademicYear';
import type { AcademicYear } from '@/models';
import { useCanOpen } from '@/hooks/useNavigation';
import { useRole } from '@/hooks/useRole';
import { portalRoutes, type AppRoute } from '@/routes';

/** The Academic Years screen: the Academic Structure workspace's `years` tab. */
const ACADEMIC_STRUCTURE_PATH = '/administrator/academics/structure';
const MANAGE_ACADEMIC_YEARS_PATH = `${ACADEMIC_STRUCTURE_PATH}?tab=years`;

/** Whether `path` is a page among `routes` (redirects excluded). */
function findRoute(routes: AppRoute[], path: string, prefix = ''): AppRoute | null {
  for (const route of routes) {
    const full = route.path ? `${prefix}/${route.path}` : prefix;
    if (full === path && route.component) return route;
    if (route.children) {
      const found = findRoute(route.children, path, full);
      if (found) return found;
    }
  }
  return null;
}

/** Toast shown after the working year changes. */
const academicYearChangedMessage = (yearName: string) =>
  `Academic year changed to ${yearName}. Data and transactions will now use this academic context.`;

/** The label a past year carries in the list: "Closed" once closed, else "Ended". */
const pastYearLabel = (year: AcademicYear): string | null =>
  year.status === 'Closed' ? 'Closed' : isPastAcademicYear(year) ? 'Ended' : null;

export interface AcademicYearSwitcherProps {
  /**
   * Shows the working year as a label with no menu, for users who view the year but
   * may not change it (students and parents).
   */
  readOnly?: boolean;
}

/**
 * Header picker for the working academic year. Lists all the school's years, newest
 * first, past ones labelled "Closed" (or "Ended"), and sets the working year for the
 * session; every year-aware page follows it. A past year can be chosen to view it,
 * but is never picked by default.
 */
export function AcademicYearSwitcher({ readOnly = false }: AcademicYearSwitcherProps = {}) {
  const {
    years,
    isLoading,
    error,
    currentYear,
    schoolYearId,
    setChosenYearId,
    missing,
    missingReason,
    endedYearName,
  } = useCurrentAcademicYear();
  const { portals } = useRole();
  const canOpenStructure = useCanOpen(ACADEMIC_STRUCTURE_PATH);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const choices = useMemo(
    () =>
      [...years].sort(
        (a, b) =>
          b.yearStartDate.localeCompare(a.yearStartDate) || b.yearName.localeCompare(a.yearName),
      ),
    [years],
  );

  // The link shows only where the role's portals have the Academic Structure page and its menus reach it.
  const canManage =
    canOpenStructure && portals.some((portal) => findRoute(portalRoutes[portal], ACADEMIC_STRUCTURE_PATH) !== null);

  if (isLoading) {
    return (
      <Box
        role="status"
        aria-label="Loading academic years"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1 }}
      >
        <Skeleton variant="circular" width={18} height={18} />
        <Skeleton variant="text" width={60} sx={{ fontSize: '0.875rem' }} />
      </Box>
    );
  }

  if (error && !currentYear) {
    return (
      <Tooltip title="Academic years could not be loaded">
        <Typography variant="caption" color="error" noWrap sx={{ px: 1 }}>
          Academic years unavailable
        </Typography>
      </Tooltip>
    );
  }

  if (!currentYear && !missing) {
    // No school yet, so there are no years to choose from.
    return (
      <Tooltip title="No academic year is set up for this school">
        <Typography variant="caption" color="text.secondary" noWrap sx={{ px: 1 }}>
          No academic year
        </Typography>
      </Tooltip>
    );
  }

  const choose = (yearId: number, yearName: string) => {
    setAnchor(null);
    if (yearId === currentYear?.yearId) return;
    setChosenYearId(yearId);
    enqueueSnackbar(academicYearChangedMessage(yearName), { variant: 'success' });
  };

  const label = currentYear ? currentYear.yearName : 'Select academic year';
  const triggerName = currentYear
    ? `Academic Year: ${currentYear.yearName}`
    : 'Select academic year';
  const caption = (
    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
      Academic Year
    </Typography>
  );

  if (readOnly) {
    // Plain visible text, read by screen readers as it reads: "Academic Year 2026-2027".
    // On small screens the "Academic Year" caption is visually hidden, the year stays.
    const yearLabel = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          py: 0.5,
          minWidth: 0,
          maxWidth: { xs: 160, md: 260 },
          color: currentYear ? 'text.secondary' : 'error.main',
        }}
      >
        <CalendarMonthOutlinedIcon fontSize="small" aria-hidden />
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ flexShrink: 0, display: { xs: 'none', md: 'block' } }}
          >
            Academic Year
          </Typography>
          <Typography
            component="span"
            sx={{
              display: { xs: 'inline', md: 'none' },
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
            }}
          >
            Academic Year
          </Typography>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 700, color: currentYear ? 'text.primary' : 'error.main' }}
          >
            {currentYear ? currentYear.yearName : 'No academic year'}
          </Typography>
        </Box>
      </Box>
    );
    // Without a year, the reason is also offered on hover.
    return currentYear ? (
      yearLabel
    ) : (
      <Tooltip title={missingAcademicYearMessage({ missingReason, endedYearName })}>
        {yearLabel}
      </Tooltip>
    );
  }
  const tooltip = currentYear
    ? 'Switch academic year'
    : missingAcademicYearMessage({ missingReason, endedYearName });

  return (
    <>
      <Tooltip title={tooltip}>
        <Box
          role="button"
          tabIndex={0}
          aria-label={triggerName}
          aria-haspopup="menu"
          aria-expanded={anchor ? 'true' : undefined}
          onClick={(e) => setAnchor(e.currentTarget)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setAnchor(e.currentTarget);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.5,
            maxWidth: { xs: 'auto', md: 260 },
            borderRadius: 2,
            cursor: 'pointer',
            color: currentYear ? 'text.secondary' : 'error.main',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <CalendarMonthOutlinedIcon fontSize="small" />
          {/* Icon-only on small screens; the name is in the accessible label and tooltip. */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'baseline',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            {currentYear && caption}
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: currentYear ? 700 : 600,
                color: currentYear ? 'text.primary' : 'error.main',
              }}
            >
              {label}
            </Typography>
          </Box>
          <ExpandMoreOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 240, maxHeight: 400 } } }}
      >
        <ListSubheader sx={{ lineHeight: 2.5 }}>Academic Year</ListSubheader>
        {choices.length === 0 && (
          <MenuItem disabled dense>
            <ListItemText primary="No academic years" />
          </MenuItem>
        )}
        {choices.map((year) => {
          const active = year.yearId === currentYear?.yearId;
          const past = pastYearLabel(year);
          return (
            <MenuItem
              key={year.yearId}
              selected={active}
              onClick={() => choose(year.yearId, year.yearName)}
            >
              <ListItemIcon sx={{ minWidth: 24 }}>
                {active && (
                  <CircleIcon titleAccess="Working year" color="primary" sx={{ fontSize: 10 }} />
                )}
              </ListItemIcon>
              <ListItemText primary={year.yearName} />
              {year.yearId === schoolYearId && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label="Current"
                  sx={{ ml: 1 }}
                />
              )}
              {past && <Chip size="small" variant="outlined" label={past} sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
        {canManage && <Divider />}
        {canManage && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              void navigate(MANAGE_ACADEMIC_YEARS_PATH);
            }}
          >
            <ListItemText primary="Manage Academic Years" />
            <ArrowForwardRoundedIcon fontSize="small" sx={{ ml: 1 }} />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
